/** Imports */
import {z} from 'zod';
import type React from 'react';
import {DateTime} from 'luxon';
import {type Role} from '@/actions/organizations';
import {parseDurationMs} from 'ms-parse-duration';
import {type Platform} from '@/mongoSchemas/Page/Pages.zod';
import {type Widget} from '@/components/Page_Reportings/Widget';
import {type MediaWithThumbnail} from '@/mongoSchemas/Post/Medias.zod';
import {type Jsonified, nullishFilter, type tFunction} from '@/types/utils';
import {getTikTokSettingsErrorMessages} from '@/modules/tiktokSettingsValidation';
import {getYouTubeTagCharacterCount, YOUTUBE_TAGS_MAX_LENGTH} from '@/modules/postEditingCapabilities';
import {type MemberRole, type Timezone} from '@/mongoSchemas/Organization/Organizations.zod';
import {type PostCreatorFeedbackMessage, postCreatorFeedbackMessage} from '@/modules/postCreatorFeedback';
import {isTikTokAnchorUrlAuthorized, type TikTokAnchorEligibility} from '@/mongoSchemas/TikTokAnchorEligibility.zod';
import {
    zTikTokAnchorLink,
    type TikTokPostSettings,
    type YouTubePostSettings,
    type FacebookPostSettings,
    type InstagramPostSettings,
} from '@/mongoSchemas/Post/Posts.zod';


/**
 * Exposes the current application version from the public environment.
 *
 * This value prefers `process.env.NEXT_PUBLIC_APP_VERSION` so it can be embedded
 * into client-side bundles. When no version is provided, it falls back to `'dev'`
 * to indicate a local or non-versioned build.
 *
 * Useful for diagnostics, UI version badges, and build metadata display.
 */
export const APP_VERSION = process.env.NEXT_PUBLIC_APP_VERSION ?? 'dev';

/**
 * Indicates whether the application is running in production mode.
 * This is based on the value of `process.env.NODE_ENV`.
 */
export const isProduction = (process.env.NODE_ENV === 'production');

/**
 * Indicates whether preview-only features should be visible.
 * Vercel exposes `NEXT_PUBLIC_VERCEL_ENV` to client bundles as production,
 * preview, or development. Local non-Vercel builds fall back to `NODE_ENV`.
 */
export const isDevelopmentOrPreviewEnvironment = (
    (process.env.NEXT_PUBLIC_VERCEL_ENV ?? process.env.VERCEL_ENV ?? (
        (process.env.NODE_ENV === 'development') ? 'development' : 'production'
    )) !== 'production'
);

export function getNumberArrayOption(value: unknown): Array<number> {
    if (!Array.isArray(value)) return [];
    return value
        .filter(nullishFilter)
        .map(Number)
        .filter((item) => Number.isInteger(item));
}

export function getStringArrayOption(value: unknown): Array<string> {
    if (!Array.isArray(value)) return [];
    return value
        .filter(nullishFilter)
        .map(String)
        .map((item) => item.trim());
}

/**
 * Resolves the application's base URL.
 *
 * In production this always uses the canonical public domain
 * `https://mediafox.social`.
 *
 * In non-production environments it prefers `process.env.NEXT_PUBLIC_APP_URL`
 * when defined, and otherwise falls back to `http://localhost:3000`.
 *
 * This value is intended for building absolute URLs and other environment-aware
 * links across the application.
 */
const configuredBaseUrl = (isProduction)
    ? 'https://mediafox.social'
    : (process.env.NEXT_PUBLIC_APP_URL)
        ? process.env.NEXT_PUBLIC_APP_URL
        : 'http://localhost:3000';
export const baseUrl = new URL(configuredBaseUrl).origin;

/**
 * Map of all known organization member roles to their corresponding role metadata.
 *
 * The keys are role IDs (including `MemberRole` values and some string literals) and
 * the values describe the role's rank, category and display label.
 */
export const allRoles: Record<MemberRole | string, Role> = {
    'owner': {
        id: 'owner',
        rank: 10,
        category: '*',
        label: 'Admin',
    },
    'approver': {
        id: 'approver',
        rank: 1,
        category: '*',
        label: 'Approver',
    },

    'user:canEdit': {
        id: 'user:canEdit',
        rank: 2,
        category: 'user',
        label: 'User: Editor',
    },
    'user:canView': {
        id: 'user:canView',
        rank: 1,
        category: 'user',
        label: 'User: Viewer',
    },

    'pages:canEdit': {
        id: 'pages:canEdit',
        rank: 2,
        category: 'pages',
        label: 'Pages: Editor',
    },
    'pages:canView': {
        id: 'pages:canView',
        rank: 1,
        category: 'pages',
        label: 'Pages: Viewer',
    },

    'posts:canEdit': {
        id: 'posts:canEdit',
        rank: 2,
        category: 'posts',
        label: 'Posts: Editor',
    },
    'posts:canView': {
        id: 'posts:canView',
        rank: 1,
        category: 'posts',
        label: 'Posts: Viewer',
    },

    'reportings:canEdit': {
        id: 'reportings:canEdit',
        rank: 2,
        category: 'reportings',
        label: 'Analytics: Editor',
    },
    'reportings:canView': {
        id: 'reportings:canView',
        rank: 1,
        category: 'reportings',
        label: 'Analytics: Viewer',
    },

    'foxlink:shortLinks:canEdit': {
        id: 'foxlink:shortLinks:canEdit',
        rank: 2,
        category: 'foxlink:shortLinks',
        label: 'FoxLink Short Links: Editor',
    },
    'foxlink:shortLinks:canView': {
        id: 'foxlink:shortLinks:canView',
        rank: 1,
        category: 'foxlink:shortLinks',
        label: 'FoxLink Short Links: Viewer',
    },
    'foxlink:linkTrees:canEdit': {
        id: 'foxlink:linkTrees:canEdit',
        rank: 2,
        category: 'foxlink:linkTrees',
        label: 'FoxLink Link Trees: Editor',
    },
    'foxlink:linkTrees:canView': {
        id: 'foxlink:linkTrees:canView',
        rank: 1,
        category: 'foxlink:linkTrees',
        label: 'FoxLink Link Trees: Viewer',
    },
} as const;

/**
 * Human-readable labels for supported social media platforms.
 */
export const platformLabels: Record<Platform, string> = {
    facebook: 'Facebook',
    instagram: 'Instagram',
    tiktok: 'TikTok',
    youtube: 'YouTube',
    threads: 'Threads',
    twitter: 'X',
};

/**
 * Configuration for sentiment display including color and emoji for each sentiment value.
 */
export const sentimentConfig = {
    POSITIVE: {
        sentiment: 'POSITIVE' as const,
        color: '#36d985',
        colorDark: '#1f9f63',
        emoji: '😊',
    },
    NEGATIVE: {
        sentiment: 'NEGATIVE' as const,
        color: '#e45a63',
        colorDark: '#9f2e3a',
        emoji: '😡',
    },
    NEUTRAL: {
        sentiment: 'NEUTRAL' as const,
        color: '#d8b94a',
        colorDark: '#e09641',
        emoji: '😐',
    },
};


/**
 * List of allowed origins for CORS / access control.
 */
export const allowedOrigins = [baseUrl];


/** Helper functions */
/**
 * Formats a numeric value as a localized string.
 *
 * Non-numeric or falsy values are treated as `0`. If the value is `Infinity`,
 * the infinity symbol (`♾️`) is returned. When `floor` is `true`, the number
 * is truncated to the specified decimal places before formatting.
 *
 * @param locale - BCP 47 locale string used for formatting.
 * @param numberInput - Value to be converted to a number and formatted.
 * @param decimals - Number of decimal places to display.
 * @param floor - Whether to truncate the value instead of rounding.
 * @returns Localized numeric string representation.
 */
export function toLocalizedNumber(locale: string, numberInput: unknown = 0, decimals: number = 0, floor: boolean = false): string {
    let number = Number(numberInput);
    if (!number || isNaN(number)) number = 0;
    if (number.toString() === 'Infinity') return '♾️';
    if (floor) number = toFixed(number, decimals);
    return number.toLocaleString(locale, {
        minimumFractionDigits: decimals,
        maximumFractionDigits: decimals,
    });
}

/**
 * Formats a number into a short, human-readable representation using the
 * appropriate unit suffix (e.g. `K`, `M`, `B`).
 *
 * Values less than 1000 are formatted without a suffix. `Infinity` yields the
 * infinity symbol (`♾️`).
 *
 * @param locale - BCP 47 locale string used for formatting.
 * @param number - The numeric value to format.
 * @param decimals - Number of decimals to use for the compact value.
 * @returns Localized compact numeric string.
 */
export function toLocalizedNumberShort(locale: string, number: number = 0, decimals: number = 1): string {
    if (!number) number = 0;
    if (number.toString() === 'Infinity') return '♾️';
    if (number >= 1_000_000_000) return `${toLocalizedNumber(locale, number / 1_000_000_000, decimals)}B`;
    if (number >= 1_000_000) return `${toLocalizedNumber(locale, number / 1_000_000, decimals)}M`;
    if (number >= 1_000) return `${toLocalizedNumber(locale, number / 1_000, decimals)}K`;
    return toLocalizedNumber(locale, number, 0);
}

/**
 * Calculates the median of a numeric array without mutating the input.
 * Non-finite values are ignored so incomplete metric data cannot skew results.
 */
export function getMedian(values: Array<number>): number {
    const sortedValues = values
        .filter((value) => Number.isFinite(value))
        .toSorted((a, b) => a - b);
    if (sortedValues.length === 0) return 0;
    const middleIndex = Math.floor(sortedValues.length / 2);
    if ((sortedValues.length % 2) === 1) return sortedValues[middleIndex]!;
    return (sortedValues[middleIndex - 1]! + sortedValues[middleIndex]!) / 2;
}

/**
 * Formats USD currency, optionally using compact notation for larger values.
 *
 * @param locale - BCP 47 locale string used for formatting.
 * @param value - Numeric USD value to format.
 * @param short - Whether large values should use compact notation.
 * @returns Localized USD currency string.
 */
export function toLocalizedCurrencyUsd(locale: string, value: number = 0, short: boolean = false): string {
    const number = (Number.isFinite(value))
        ? value
        : 0;
    const useCompactNotation = short && (Math.abs(number) >= 1_000);
    return new Intl.NumberFormat(locale, {
        style: 'currency',
        currency: 'USD',
        notation: (useCompactNotation)
            ? 'compact'
            : 'standard',
        minimumFractionDigits: (useCompactNotation)
            ? 0
            : 2,
        maximumFractionDigits: (useCompactNotation)
            ? 1
            : 2,
    }).format(number);
}

/**
 * Formats a fractional value as a localized percentage.
 *
 * @param locale - BCP 47 locale string used for formatting.
 * @param value - Fractional value where `1` represents 100%.
 * @param short - Whether to omit decimal places for compact chart labels.
 * @returns Localized percentage string.
 */
export function toLocalizedPercentage(locale: string, value: number = 0, short: boolean = false): string {
    const number = (Number.isFinite(value))
        ? value
        : 0;
    return new Intl.NumberFormat(locale, {
        style: 'percent',
        minimumFractionDigits: (short) ? 0 : 2,
        maximumFractionDigits: (short) ? 0 : 2,
    }).format(number);
}

/**
 * Converts a byte count into a human-readable size string using binary units
 * (KB, MB, GB, ... up to YB).
 *
 * @param bytes - Number of bytes.
 * @param decimals - Number of decimal places to include in the formatted value.
 * @returns Human-readable file size string.
 */
export function bytesToSize(bytes: number, decimals: number = 2): string {
    if (bytes <= 0) return '0 Bytes';
    const k = 1_024;
    const dm = (decimals < 0) ? 0 : decimals;
    const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return `${(bytes / k ** i).toFixed(dm)} ${sizes[i]}`;
}

/**
 * Truncates a number to a fixed number of decimal places without rounding.
 *
 * @param num - The number to truncate.
 * @param fixed - Number of decimal places to keep.
 * @returns The truncated number, or `0` if parsing fails.
 */
export function toFixed(num: number, fixed: number): number {
    if (!Number.isFinite(num) || !Number.isFinite(fixed)) return 0;
    const digits = Math.max(0, Math.trunc(fixed));
    const factor = 10 ** digits;
    return Math.trunc(num * factor) / factor;
}

/**
 * Deep clone an object by converting it to JSON and back.
 * Should be used before sending data to the client to remove any methods or prototypes (e.g. MongoDB ObjectIDs),
 * e.g. from server components to client components
 * Not the most efficient way, but works for most use cases.
 *
 * @template T The type of the object to clone.
 * @param object The object to clone.
 * @returns A deep clone of the object.
 */
export function toJSON<T>(object: T): Jsonified<T> {
    if (object === undefined) return undefined as unknown as Jsonified<T>;
    return JSON.parse(JSON.stringify(object));
}

/**
 * Convert milliseconds to a time string (HH:MM:SS or MM:SS or SS).
 *
 * @param ms The time in milliseconds.
 * @returns The formatted time string.
 */
export function toTime(ms: number): string {
    const seconds = ms / 1_000;
    const hours = Math.floor(seconds / 3_600);
    const minutes = Math.floor((seconds % 3_600) / 60);
    const remainingSeconds = Math.round(seconds % 60);

    // Seconds only
    if (seconds < 60) return `${remainingSeconds}s`;

    // With minutes
    if (hours === 0) return `${String(minutes)
        .padStart(2, '0')}:${String(remainingSeconds)
        .padStart(2, '0')}`;

    // With hours
    return `${String(hours)
        .padStart(2, '0')}:${String(minutes)
        .padStart(2, '0')}:${String(remainingSeconds)
        .padStart(2, '0')}`;
}

/**
 * Creates a localized date formatting helper based on the provided translation function.
 *
 * The returned function accepts a date-like value, an optional organization timezone
 * and a mode indicating whether to format date, time or both.
 *
 * @param t - Translation function providing format templates for date/time.
 * @returns Function that formats a given date according to the configured templates.
 */
export function dateFormater(t: tFunction): (
    date: Date | any,
    timezone?: Timezone,
    mode?: boolean | 'timeOnly' | 'dateOnly',
) => string {
    const formatDate = t('date.formatDate');
    const formatTime = t('date.formatTime');
    const fallback = t('date.invalid');
    return (date: Date | any, timezone?: Timezone, mode: boolean | 'timeOnly' | 'dateOnly' = false) =>
        dateFormat(formatDate, formatTime, date, mode, fallback, timezone);
}

/**
 * Receives strings in dd-mm format and returns the formatted date according to the organization timezone and locale.
 * Returns strings in the format "Month Day" (e.g. "January 1") according to the organization timezone and locale.
 *
 * @param date
 * @param locale
 * @param timezone
 */
export function dateFormatEvergreen(
    date: string,
    locale: string,
    timezone?: Timezone,
): string {

    // Create a Luxon DateTime object from the input date string (assuming it's in "dd-MM" format)
    const [day, month] = date.split('-').map(Number);
    const displayYear = 2024; // Use a fixed display year since the actual year is not relevant for formatting
    const luxonDate = DateTime.fromObject({day, month, year: displayYear}, {zone: timezone, locale});

    // Format date here, dont use the dateFormat function
    if (!luxonDate.isValid) return '-';
    return luxonDate.toLocaleString({month: 'long', day: 'numeric'});
}


/**
 * Formats a date using the given date and time format strings.
 *
 * @param formatDate - Date format string.
 * @param formatTime - Time format string.
 * @param date - Date object or date-like value.
 * @param mode - Format mode: `true` for date only, `false` for date and time, `'timeOnly'` for time only, `'dateOnly'` for date only.
 * @param fallback - Fallback string for invalid dates.
 * @param timezone - Optional timezone identifier.
 * @returns Formatted date string.
 */
function dateFormat(
    formatDate: string,
    formatTime: string,
    date: Date | any,
    mode: boolean | 'timeOnly' | 'dateOnly',
    fallback: string,
    timezone?: Timezone,
): string {

    let luxonDate = date;

    // check if luxonDate is not a luxon DateTime instance
    if (!(luxonDate instanceof DateTime)) {
        luxonDate = (typeof date === 'string')
            ? DateTime.fromISO(date, {zone: timezone})
            : DateTime.fromJSDate(date, {zone: timezone});
    }

    // Validate
    if (!luxonDate.isValid) return fallback;

    // Parts
    const year = luxonDate.toFormat('yyyy');
    const month = luxonDate.toFormat('MM');
    const day = luxonDate.toFormat('dd');
    const hour = luxonDate.toFormat('HH');
    const minute = luxonDate.toFormat('mm');
    const second = luxonDate.toFormat('ss');

    // Format
    const datePart = formatDate
        .replaceAll('YYYY', year)
        .replaceAll('MM', month)
        .replaceAll('DD', day);
    const timePart = formatTime
        .replaceAll('HH', hour)
        .replaceAll('mm', minute)
        .replaceAll('ss', second);

    // Return
    if (mode === 'timeOnly') return timePart;
    if (mode === 'dateOnly') return datePart;
    return `${datePart}${(!mode) ? ` ${timePart}` : ''}`;
}

/**
 * Checks whether the given permission list grants at least one of the target permissions.
 *
 * The special `owner` role always grants access. The `targetPermission` can be a single permission or an array of acceptable permissions.
 *
 * @param targetPermission - Permission(s) to check for.
 * @param permissionList - List of permissions the user currently has.
 * @returns `true` when the user has a matching permission, otherwise `false`.
 */
export function hasPermission(targetPermission: MemberRole | Array<MemberRole>, permissionList: Array<MemberRole> | undefined): boolean {
    if (!permissionList) return false;
    const targetPermissionArray = (Array.isArray(targetPermission)) ? targetPermission : [targetPermission];
    return permissionList.includes('owner')
        || permissionList.some((permission) => targetPermissionArray.includes(permission));
}

/**
 * Retrieves the role metadata for a given role string.
 *
 * @param roleString - Role identifier string (e.g. 'owner', 'user:canEdit').
 * returns The corresponding Role object if found, otherwise `null`.
 */
export function roleFromString(roleString: MemberRole): Role | null {
    return allRoles[roleString] || null;
}

/**
 * Checks whether any of the given roles has a rank equal to or higher than the specified role.
 *
 * @param allRoles - List of roles to check.
 * @param checkRole - Role to compare against.
 * @returns `true` if at least one role has equal or higher rank in the same category, otherwise `false`.
 */
export function hasEqualOrHigherRole(allRoles: Array<Role>, checkRole: Role | MemberRole): boolean {
    if (!allRoles) return false;
    const checkRoleObj = (typeof checkRole === 'string')
        ? roleFromString(checkRole)
        : checkRole;
    if (!checkRoleObj) return false;
    return allRoles.some((role) =>
        ((role.category === checkRoleObj.category) || (role.category === '*'))
        && (role.rank >= checkRoleObj.rank));
}

/**
 * Returns a promise that resolves after the given delay in milliseconds.
 *
 * @param ms - Delay duration in milliseconds.
 * @returns Promise that resolves once the delay has elapsed.
 */
export function delay(ms: number): Promise<void> {
    return new Promise((resolve) => setTimeout(resolve, ms));
}

/**
 * Converts a date to a local ISO string (up to minutes) by offsetting it with the
 * current environment's timezone offset.
 *
 * Note: when a `Date` instance is provided it is mutated before being formatted.
 *
 * @param date - Date object or ISO string representation.
 * @returns ISO string in the form `YYYY-MM-DDTHH:mm`.
 */
export function toLocalISOString(date: Date | string): string {
    const parsedDate = (typeof date === 'string')
        ? new Date(date)
        : date;
    if (Number.isNaN(parsedDate.getTime())) return '';
    // Use local-time getters so we never mutate the original Date object
    // and avoid the UTC ↔ local round-trip that caused drift on every render.
    const pad = (n: number) => String(n).padStart(2, '0');
    return (
        `${parsedDate.getFullYear()}-${pad(parsedDate.getMonth() + 1)}-${pad(parsedDate.getDate())}` +
        `T${pad(parsedDate.getHours())}:${pad(parsedDate.getMinutes())}`
    );
}

/**
 * Formats a numeric value for use in charts according to the widget's data type configuration.
 *
 * @param locale - BCP 47 locale string used for formatting.
 * @param value - Numeric value to format.
 * @param format - Widget data type format (integer, decimal or percentage).
 * @param short - Whether to use a compact representation.
 * @returns Localized formatted value string.
 */
export function formatChartValue(locale: string, value: number, format: Widget['dataTypeFormat'], short = false): string {

    // Integer
    if (format === 'INTEGER') {
        if (short) return toLocalizedNumberShort(locale, value, 0);
        return toLocalizedNumberShort(locale, value, 2);
    }

    // Decimal
    else if (format === 'DECIMAL') {
        if (short) return toLocalizedNumberShort(locale, value, 2);
        return toLocalizedNumber(locale, value, 2);
    }

    // Percentage
    else if (format === 'PERCENTAGE') {
        return toLocalizedPercentage(locale, value, short);
    }

    // USD currency
    else if (format === 'CURRENCY_USD') {
        return toLocalizedCurrencyUsd(locale, value, short);
    }

    // Default
    if (short) return toLocalizedNumberShort(locale, value, 0);
    return toLocalizedNumber(locale, value, 2);
}

/**
 * Checks whether the given media list contains at least one video.
 *
 * @param mediaList - List of media items with metadata.
 * @returns `true` if at least one item is a video, otherwise `false`.
 */
export function mediaListHasVideo(mediaList: Array<MediaWithThumbnail>): boolean {
    return mediaList.some(({media}) => (media?.type === 'video'));
}

/**
 * Checks whether the given media list contains at least one image.
 *
 * @param mediaList - List of media items with metadata.
 * @returns `true` if at least one item is an image, otherwise `false`.
 */
export function mediaListHasImage(mediaList: Array<MediaWithThumbnail>): boolean {
    return mediaList.some(({media}) => (media?.type === 'image'));
}

/**
 * Checks whether the given media list contains a mix of images and videos.
 *
 * @param mediaList - List of media items with metadata.
 * @returns `true` if both images and videos are present, otherwise `false`.
 */
export function mediaListIsMixed(mediaList: Array<MediaWithThumbnail>): boolean {
    return mediaListHasVideo(mediaList) && mediaListHasImage(mediaList);
}

/**
 * Indicates whether the media list is empty.
 *
 * @param mediaList - List of media items with metadata.
 * @returns `true` if the list has no items, otherwise `false`.
 */
export function mediaListIsEmpty(mediaList: Array<MediaWithThumbnail>): boolean {
    return (mediaList.length === 0);
}


/** Constants */
const facebookStoryVideoMaxSeconds = 60;

/**
 * Validates a Facebook post draft and returns a list of translated error messages for media, text and option constraints.
 *
 * @param t - Translation function for error keys.
 * @param mediaList - List of attached media.
 * @param caption - Post caption text.
 * @param postSettings - Platform-specific posting settings.
 * @returns Array of error messages describing all validation problems.
 */
export function getFacebookErrors(
    t: tFunction,
    mediaList: Array<MediaWithThumbnail>,
    caption: string,
    postSettings: FacebookPostSettings | undefined,
): Array<PostCreatorFeedbackMessage> {
    const errors: Array<PostCreatorFeedbackMessage> = [];

    // Media errors
    if (mediaList.length > 99) {
        errors.push(postCreatorFeedbackMessage(t('facebookMaximumMedia'), 'facebook'));
    }
    if ((mediaList.length > 1) && mediaListHasVideo(mediaList)) {
        errors.push(postCreatorFeedbackMessage(t('facebookCarouselImages'), 'facebook'));
    }
    if ((postSettings?.postingType === 'feed') && (getLongestMediaInSeconds(mediaList) > 240 * 60)) {
        errors.push(postCreatorFeedbackMessage(t('facebookLongestVideoTooLong240Min'), 'facebook'));
    }
    if ((postSettings?.postingType === 'reel') && (getLongestMediaInSeconds(mediaList) > 90)) {
        errors.push(postCreatorFeedbackMessage(t('facebookLongestVideoTooLong90Sec'), 'facebook'));
    }
    if ((postSettings?.postingType === 'story')
        && mediaListHasVideo(mediaList)
        && (getLongestMediaInSeconds(mediaList) > facebookStoryVideoMaxSeconds)
    ) {
        errors.push(postCreatorFeedbackMessage(
            t('facebookLongestVideoTooLongStory', {max: facebookStoryVideoMaxSeconds}),
            'facebook',
        ));
    }
    if ((postSettings?.postingType === 'reel') && !((mediaList.length === 1) && mediaListHasVideo(mediaList))
        || (postSettings?.postingType === 'story') && !(mediaList.length === 1)
    ) {
        errors.push(postCreatorFeedbackMessage(t('facebookBadPostingType'), 'facebook'));
    }

    // Text errors
    if (caption.length > 63_206) {
        errors.push(postCreatorFeedbackMessage(t('facebookCaptionTooLong'), 'facebook'));
    }
    if ((caption.length === 0) && mediaListIsEmpty(mediaList)) {
        errors.push(postCreatorFeedbackMessage(t('facebookAddMediaOrCaption'), 'facebook'));
    }

    // Return
    return errors;
}

/**
 * Validates an Instagram post draft and returns translated error messages for media, caption and hashtag constraints.
 *
 * @param t - Translation function for error keys.
 * @param mediaList - List of attached media.
 * @param caption - Post caption text.
 * @param postSettings - Platform-specific posting settings.
 * @returns Array of error messages describing all validation problems.
 */
export function getInstagramErrors(
    t: tFunction,
    mediaList: Array<MediaWithThumbnail>,
    caption: string,
    postSettings: InstagramPostSettings | undefined,
): Array<PostCreatorFeedbackMessage> {
    const errors: Array<PostCreatorFeedbackMessage> = [];

    // Media errors
    if (mediaList.length > 10) {
        errors.push(postCreatorFeedbackMessage(t('instagramMaximumMedia'), 'instagram'));
    }
    if (mediaListIsEmpty(mediaList)) {
        errors.push(postCreatorFeedbackMessage(t('instagramAddMedia'), 'instagram'));
    }
    if (getLongestMediaInSeconds(mediaList) > 180) {
        errors.push(postCreatorFeedbackMessage(t('instagramLongestVideoTooLong'), 'instagram'));
    }
    if ((postSettings?.postingType === 'reel') && !((mediaList.length === 1) && mediaListHasVideo(mediaList))
        || (postSettings?.postingType === 'story') && !(mediaList.length === 1)
    ) {
        errors.push(postCreatorFeedbackMessage(t('instagramBadPostingType'), 'instagram'));
    }

    // Text errors
    if (caption.length > 2_200) {
        errors.push(postCreatorFeedbackMessage(t('instagramCaptionTooLong'), 'instagram'));
    }
    const hashtagCount = caption.match(/#[\w-]+/gm)?.length || 0;
    if (hashtagCount > 30) {
        errors.push(postCreatorFeedbackMessage(t('instagramMaximumHashtags'), 'instagram'));
    }

    // Return
    return errors;
}

/**
 * Validates a TikTok post draft and returns translated error messages for media, text and option constraints based on the creator information.
 *
 * @param t - Translation function for error keys.
 * @param mediaList - List of attached media.
 * @param caption - Post caption text.
 * @param description - Optional description used for image posts.
 * @param postSettings - Platform-specific posting settings including creator info.
 * @param anchorEligibility - Optional TikTok anchor eligibility information for validating anchor links.
 * @returns Array of error messages describing all validation problems.
 */
export function getTikTokErrors(
    t: tFunction,
    mediaList: Array<MediaWithThumbnail>,
    caption: string,
    description: string,
    postSettings: TikTokPostSettings | undefined,
    anchorEligibility?: Pick<TikTokAnchorEligibility, 'isEligible' | 'authorizedDomains'>,
): Array<PostCreatorFeedbackMessage> {
    const errors: Array<PostCreatorFeedbackMessage> = [];

    // General
    if (!postSettings || !postSettings.tiktokCreatorInfo) {
        return [postCreatorFeedbackMessage(t('tiktokCreatorInfoError'), 'tiktok')];
    }

    // Media errors
    if (mediaListIsEmpty(mediaList)) {
        errors.push(postCreatorFeedbackMessage(t('tiktokAddMedia'), 'tiktok'));
    }
    if (mediaListHasVideo(mediaList) && !mediaListHasImage(mediaList) && (mediaList.length > 1)) {
        errors.push(postCreatorFeedbackMessage(t('tiktokOnlyOneVideo'), 'tiktok'));
    }
    if (mediaListHasImage(mediaList) && (mediaList.length > 35)) {
        errors.push(postCreatorFeedbackMessage(t('tiktokMaximumImages'), 'tiktok'));
    }
    if (mediaListIsMixed(mediaList)) {
        errors.push(postCreatorFeedbackMessage(t('tiktokMixedMediaNotAllowed'), 'tiktok'));
    }
    if (getLongestMediaInSeconds(mediaList) > postSettings.tiktokCreatorInfo.max_video_post_duration_sec) {
        errors.push(postCreatorFeedbackMessage(
            t('tiktokLongestVideoTooLong', {max: postSettings.tiktokCreatorInfo.max_video_post_duration_sec}),
            'tiktok',
        ));
    }

    // Text errors
    if (mediaListHasVideo(mediaList)) {
        if (caption.length > 2_200) {
            errors.push(postCreatorFeedbackMessage(t('tiktokCaptionTooLong'), 'tiktok'));
        }
    }
    if (mediaListHasImage(mediaList)) {
        if (caption.length > 90) {
            errors.push(postCreatorFeedbackMessage(t('tiktokCaptionTooLong'), 'tiktok'));
        }
        if (description.length > 4_000) {
            errors.push(postCreatorFeedbackMessage(t('tiktokDescriptionTooLong'), 'tiktok'));
        }
    }

    // Link anchor errors
    const anchorLink = postSettings.tiktokAnchorLink?.trim() ?? '';
    const anchorCta = postSettings.tiktokAnchorCta?.trim() ?? '';
    if (anchorCta && !anchorLink) {
        errors.push(postCreatorFeedbackMessage(t('tiktokAnchorLinkRequired'), 'tiktok'));
    }
    const parsedAnchorLink = (anchorLink) ? zTikTokAnchorLink.safeParse(anchorLink) : undefined;
    if (parsedAnchorLink && !parsedAnchorLink.success) {
        errors.push(postCreatorFeedbackMessage(t('tiktokAnchorLinkInvalid'), 'tiktok'));
    }
    if (parsedAnchorLink?.success && !anchorEligibility) {
        errors.push(postCreatorFeedbackMessage(t('tiktokAnchorEligibilityMissing'), 'tiktok'));
    } else if (parsedAnchorLink?.success && !anchorEligibility?.isEligible) {
        errors.push(postCreatorFeedbackMessage(t('tiktokAnchorIneligible'), 'tiktok'));
    } else if (
        parsedAnchorLink?.success
        && anchorEligibility
        && !isTikTokAnchorUrlAuthorized(parsedAnchorLink.data, anchorEligibility.authorizedDomains)
    ) {
        errors.push(postCreatorFeedbackMessage(t('tiktokAnchorDomainNotAuthorized'), 'tiktok'));
    }
    if (anchorCta.length > 35) {
        errors.push(postCreatorFeedbackMessage(t('tiktokAnchorCtaTooLong'), 'tiktok'));
    }

    // Options errors
    errors.push(...getTikTokSettingsErrorMessages(t, postSettings));

    // Return
    return errors;
}

/**
 * Validates a YouTube video draft and returns translated error messages for media, title, description, tags and option constraints.
 *
 * @param t - Translation function for error keys.
 * @param mediaList - List of attached media.
 * @param caption - Video title.
 * @param description - Video description.
 * @param hashtags - List of tags/hashtags.
 * @param postSettings - Platform-specific posting settings.
 * @returns Array of error messages describing all validation problems.
 */
export function getYouTubeErrors(
    t: tFunction,
    mediaList: Array<MediaWithThumbnail>,
    caption: string,
    description: string,
    hashtags: Array<string>,
    postSettings: YouTubePostSettings | undefined,
): Array<PostCreatorFeedbackMessage> {
    const errors: Array<PostCreatorFeedbackMessage> = [];

    // Media errors
    if (mediaListIsEmpty(mediaList)) {
        errors.push(postCreatorFeedbackMessage(t('youtubeAddVideoFirst'), 'youtube'));
    } else if (mediaList.length > 1) {
        errors.push(postCreatorFeedbackMessage(t('youtubeOnlyOneVideo'), 'youtube'));
    } else if (!mediaListHasVideo(mediaList)) {
        errors.push(postCreatorFeedbackMessage(t('youtubeOnlyVideos'), 'youtube'));
    }
    if (getLongestMediaInSeconds(mediaList) > 12 * 60 * 60) {
        errors.push(postCreatorFeedbackMessage(t('youtubeLongestVideoTooLong'), 'youtube'));
    }

    // Text errors
    if (caption.length === 0) {
        errors.push(postCreatorFeedbackMessage(t('youtubeTitleRequired'), 'youtube'));
    } else if (caption.length > 100) {
        errors.push(postCreatorFeedbackMessage(t('youtubeTitleTooLong'), 'youtube'));
    }
    if (new TextEncoder().encode(description).length > 5_000) {
        errors.push(postCreatorFeedbackMessage(t('youtubeDescriptionTooLong'), 'youtube'));
    }
    if (getYouTubeTagCharacterCount(hashtags) > YOUTUBE_TAGS_MAX_LENGTH) {
        errors.push(postCreatorFeedbackMessage(t('youtubeTagsTooLong'), 'youtube'));
    }

    // Options errors
    if (!postSettings?.visibility) {
        errors.push(postCreatorFeedbackMessage(t('youtubeSelectVisibility'), 'youtube'));
    }
    if (!postSettings?.categoryId) {
        errors.push(postCreatorFeedbackMessage(t('youtubeSelectCategory'), 'youtube'));
    }

    // Return
    return errors;
}

/**
 * Validates a Threads post draft and returns translated error messages for media and caption constraints.
 *
 * @param t - Translation function for error keys.
 * @param mediaList - List of attached media.
 * @param caption - Post caption text.
 * @returns Array of error messages describing all validation problems.
 */
export function getThreadsErrors(
    t: tFunction,
    mediaList: Array<MediaWithThumbnail>,
    caption: string,
): Array<PostCreatorFeedbackMessage> {
    const errors: Array<PostCreatorFeedbackMessage> = [];

    // Media errors
    if (getLongestMediaInSeconds(mediaList) > 5 * 60) {
        errors.push(postCreatorFeedbackMessage(t('threadsLongestVideoTooLong5Min'), 'threads'));
    }
    if (mediaList.length > 20) {
        errors.push(postCreatorFeedbackMessage(t('threadsMaximumMedia'), 'threads'));
    }

    // Text errors
    if (caption.length > 500) {
        errors.push(postCreatorFeedbackMessage(t('threadsCaptionTooLong'), 'threads'));
    }
    if ((caption.length === 0) && mediaListIsEmpty(mediaList)) {
        errors.push(postCreatorFeedbackMessage(t('threadsAddMediaOrCaption'), 'threads'));
    }

    // Return
    return errors;
}

/**
 * Validates a Twitter / X post draft and returns translated error messages for media and caption constraints.
 *
 * @param t - Translation function for error keys.
 * @param mediaList - List of attached media.
 * @param caption - Post caption text.
 * @returns Array of error messages describing all validation problems.
 */
export function getTwitterErrors(
    t: tFunction,
    mediaList: Array<MediaWithThumbnail>,
    caption: string,
): Array<PostCreatorFeedbackMessage> {
    const errors: Array<PostCreatorFeedbackMessage> = [];

    // Media errors
    if (mediaList.length > 4) {
        errors.push(postCreatorFeedbackMessage(t('twitterMaximumMedia'), 'twitter'));
    }
    if (getLongestMediaInSeconds(mediaList) > 140) {
        errors.push(postCreatorFeedbackMessage(t('twitterLongestVideoTooLong'), 'twitter'));
    }

    // Text errors
    if (caption.length > 280) {
        errors.push(postCreatorFeedbackMessage(t('twitterCaptionTooLong'), 'twitter'));
    }
    if ((caption.length === 0) && mediaListIsEmpty(mediaList)) {
        errors.push(postCreatorFeedbackMessage(t('twitterAddMediaOrCaption'), 'twitter'));
    }

    // Return
    return errors;
}

/**
 * Returns the longest video duration (in seconds) from the provided media list.
 * If the list is empty or contains no durations, `0` is returned.
 *
 * @param mediaList - List of media items with optional video duration metadata.
 * @returns Maximum video duration in seconds.
 */
function getLongestMediaInSeconds(mediaList: Array<MediaWithThumbnail>): number {
    const longestMedia = mediaList.reduce((longest, current) => {
        if ((current.media?.videoDuration || 0) > (longest.media?.videoDuration || 0)) {
            return current;
        }
        return longest;
    }, mediaList[0]);
    return longestMedia?.media?.videoDuration || 0;
}

/**
 * Repeats the given string a specified number of times and joins the occurrences with an optional separator.
 *
 * @param str - String to repeat.
 * @param times - Number of repetitions.
 * @param joinStr - Separator used between repetitions.
 * @returns The concatenated result string.
 */
export function repeat(str: string, times: number, joinStr = ''): string {
    return new Array(times)
        .fill(str)
        .join(joinStr);
}

/**
 * Resolves the duration of a video source in seconds by loading its metadata into a temporary HTMLVideoElement.
 *
 * A timeout of 3 seconds is applied; if the metadata is not loaded within this time, the function resolves with `0` seconds.
 * When a `File` is used as source, an object URL is created and revoked once the duration has been determined or the call times out.
 * Note that this function can only be used in browser/client environments, to use it in SSR contexts use the function with same name from "`@/modules/serverUtils`".
 *
 * @param videoSrc - File object or URL string referencing the video.
 * @returns Promise resolving to the video duration in seconds.
 */
export function getVideoDuration(videoSrc: File | string): Promise<number> {
    return new Promise<number>((resolve, reject) => {
        if ((typeof window === 'undefined') || !('document' in window)) {
            throw new Error('getVideoDuration can only be used in browser environments');
        }
        let isResolved = false;
        let video: HTMLVideoElement | null = null;
        let timeout: ReturnType<typeof setTimeout> | null = null;
        try {
            const createdVideo = document.createElement('video');
            video = createdVideo;
            timeout = setTimeout(() => {
                if (isResolved) return;
                isResolved = true;
                console.warn('getVideoDuration: timeout');
                if (videoSrc instanceof File) {
                    window.URL.revokeObjectURL(createdVideo.src);
                }
                createdVideo.remove();
                resolve(0);
            }, parseDurationMs('3s'));
            createdVideo.preload = 'metadata';
            createdVideo.muted = true;
            createdVideo.playsInline = true;
            createdVideo.onloadedmetadata = () => {
                if (isResolved) return;
                isResolved = true;
                if (timeout !== null) clearTimeout(timeout);
                const duration = createdVideo.duration;
                if (videoSrc instanceof File) {
                    window.URL.revokeObjectURL(createdVideo.src);
                }
                createdVideo.remove();
                resolve(duration);
            };
            createdVideo.onerror = () => {
                if (isResolved) return;
                isResolved = true;
                if (timeout !== null) clearTimeout(timeout);
                if (videoSrc instanceof File) {
                    window.URL.revokeObjectURL(createdVideo.src);
                }
                createdVideo.remove();
                resolve(0);
            };
            createdVideo.src = (videoSrc instanceof File)
                ? URL.createObjectURL(videoSrc)
                : videoSrc;
        } catch (error) {
            console.error(error);
            if (timeout) clearTimeout(timeout);
            video?.remove();
            isResolved = true;
            reject(error);
        }
    });
}

/**
 * Escapes special regular expression characters in the provided string so that it can be safely used inside a {@link RegExp} pattern.
 *
 * @param string - Source string to escape.
 * @returns Escaped string safe for use in regular expressions.
 */
export function escapeRegExp(string: string): string {
    z.string().parse(string);
    return string.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

/**
 * Converts a `HH:mm` time string into the corresponding number of minutes.
 *
 * @param timeString - Time string in 24-hour `HH:mm` format.
 * @returns Total number of minutes represented by the time.
 */
export function timeToMinutes(timeString: string): number {
    z.string().parse(timeString);
    const [hours, minutes] = timeString
        .split(':')
        .map(Number);
    return hours * 60 + minutes;
}

/**
 * Rotates an array by `k` positions without mutating the original array.
 *
 * Positive values rotate to the left, negative values rotate to the right.
 * For an empty array, a new empty array is returned.
 *
 * @param array - Array to rotate.
 * @param k - Number of positions to rotate by.
 * @returns New array with rotated elements.
 */
export function arrayRotate<T>(array: Array<T>, k: number): Array<T> {
    z.number().parse(k);
    const n = array.length;
    if (n === 0) return [];
    const r = ((k % n) + n) % n;
    return array.slice(r).concat(array.slice(0, r));
}

/**
 * Safely parses a JSON string and returns the result typed as `T`.
 *
 * On parse error the function logs to the console and returns `null`.
 * Note that the generic type `T` is not validated at runtime and should be considered a hint for TypeScript only.
 *
 * @param jsonString - JSON string to parse.
 * @returns Parsed value on success, or `null` on failure.
 */
export function tryParseJson<T>(jsonString: string): T | null {
    try {
        return JSON.parse(jsonString) as T;
    } catch (error) {
        console.error('Error parsing JSON string', {jsonString, error});
        return null;
    }
}

/**
 * Attempts to create a case-insensitive regular expression from the provided pattern string.
 *
 * @param pattern - Pattern string from which to create the regular expression.
 * @returns A {@link RegExp} instance on success, or `null` when the pattern is falsy or invalid.
 */
export function tryParseRegex(pattern: string | undefined): RegExp | null {
    if (!pattern) return null;
    try {
        return new RegExp(pattern, 'i');
    } catch {
        return null;
    }
}

/**
 * Truncates a string to the specified maximum length and appends an ellipsis when truncation occurs.
 *
 * If `maxLength` is less than 4, the string is simply cut to the given length without appending an ellipsis.
 *
 * @param input - Input string to truncate.
 * @param maxLength - Maximum allowed length of the returned string.
 * @returns Truncated string with or without ellipsis.
 */
export function ellipsis(input: string, maxLength: number): string {
    if (maxLength < 4) return input.substring(0, maxLength);
    if (input.length <= maxLength) return input;
    return `${input.substring(0, maxLength - 3)}...`;
}

/**
 * Returns a new array with one element moved from one index to another.
 *
 * The original array is not mutated. The item at `from` is removed from its
 * current position and inserted at `to` in the copied array.
 *
 * @typeParam T - The array element type.
 * @param array - Source array to copy and reorder.
 * @param from - Zero-based index of the item to move.
 * @param to - Zero-based destination index for the moved item.
 * @returns A new array with the specified item repositioned.
 */
export function arrayMove<T>(array: Array<T>, from: number, to: number): Array<T> {
    const newArray = array.slice();
    const [movedItem] = newArray.splice(from, 1);
    newArray.splice(to, 0, movedItem);
    return newArray;
}


/**
 * Escapes HTML-sensitive characters in a string so it can be safely rendered as text.
 *
 * Converts the following characters to their corresponding HTML entities:
 * `&`, `<`, `>`, `"`, and `'`.
 *
 * If the input is `null`, `undefined`, or an empty string, an empty string is returned.
 *
 * @param str - The string to escape.
 * @returns The escaped HTML string, or an empty string when the input is falsy.
 */
export function escapeHtml(str: string | null | undefined): string {
    if (!str) return '';
    return str
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#039;');
}

/**
 * Validates a URL for iframe usage and only allows HTTP or HTTPS sources.
 *
 * If the provided URL is valid and uses the `http:` or `https:` protocol,
 * the original link is returned unchanged. For invalid URLs or disallowed
 * protocols, the function falls back to `about:blank`.
 *
 * @param link - The URL string to validate for use as an iframe `src`.
 * @returns The original URL when safe, otherwise `about:blank`.
 */
export function getSafeIframeSrc(link: string): string {
    try {
        const url = new URL(link);
        if ((url.protocol === 'http:') || (url.protocol === 'https:')) {
            return link;
        }
    } catch {
        console.error('Invalid URL:', link);
    }
    return 'about:blank';
}

/**
 * Normalizes a search parameter value into a single string.
 *
 * Accepts a value that may be a single string, an array of strings, or
 * `undefined`, and converts it into a predictable result:
 * - returns `null` when the value is missing or falsy
 * - returns the first entry when the value is an array
 * - otherwise returns the string value as-is
 *
 * This is useful when working with query parameters that may be provided in
 * either singular or repeated form.
 *
 * @param value - Search parameter value to normalize.
 * @returns The normalized string value, or `null` when no usable value exists.
 */
export function normalizeSearchParam(value: string | Array<string> | undefined): string | null {
    if (!value) return null;
    const normalizedValue = (Array.isArray(value))
        ? (value)
            .filter(nullishFilter)
            .find((entry) => ((typeof entry === 'string') && (entry.trim().length > 0)))
        : value;
    if (typeof normalizedValue !== 'string') return null;
    const trimmed = normalizedValue.trim();
    if (!trimmed) return null;
    const parsed = z.string().safeParse(trimmed);
    return (parsed.success)
        ? parsed.data
        : null;
}

/**
 * Creates a helper that converts UTC dates into the given organization timezone.
 *
 * The returned function accepts an optional date-like value and returns a Luxon {@link DateTime} instance in the specified timezone.
 *
 * @param timezone - Organization timezone identifier.
 * @returns Function that converts UTC dates into the organization timezone.
 */
export function createToOrganizationTimeFn(timezone: Timezone): (utcDate?: Date | string | DateTime) => DateTime {
    return (utcDate?: Date | string | DateTime): DateTime => {
        if (!utcDate) return DateTime.now().setZone(timezone);
        if (utcDate instanceof DateTime) return utcDate.setZone(timezone);
        const convertedTime = DateTime
            .fromISO(
                (typeof utcDate === 'string')
                    ? utcDate
                    : utcDate.toISOString(), {zone: 'utc'},
            )
            .setZone(timezone);
        if (!convertedTime.isValid) {
            throw new Error(`Invalid date: ${utcDate}`);
        }
        return convertedTime;
    };
}

/**
 * Handles keyboard activation for accessible interactive elements.
 *
 * This helper is intended for elements that behave like buttons or links via keyboard.
 * It only responds when the key event originates from the element itself
 * (`event.currentTarget === event.target`), preventing child elements from triggering
 * the action unintentionally.
 *
 * Supported keys:
 * - `Enter`
 * - `Space` (`' '`)
 *
 * When one of these keys is pressed, the default browser behavior is prevented and
 * the provided `action` callback is executed.
 *
 * @param event - The keyboard event fired by a React element.
 * @param action - Callback to execute when the activation key is pressed.
 */
export function accessibleKeyDownHandler(event: React.KeyboardEvent, action: () => void): void {
    if (event.currentTarget !== event.target) return;
    if ((event.key === 'Enter') || (event.key === ' ')) {
        event.preventDefault();
        action();
    }
}

/**
 * Clamps a numeric score into the configured confidence range.
 *
 * @param value Raw score to clamp.
 * @param min Minimum allowed score (inclusive).
 * @param max Maximum allowed score (inclusive).
 * @returns Clamped score.
 */
export function clamp(value: number, min: number, max: number): number {
    return Math.min(max, Math.max(min, value));
}

export function getProxiedImage(url: string | null) {
    if (!url) return null;
    return `https://cdn.influ.gg/proxy-media?url=${encodeURIComponent(url)}`;
}
